import { Menu } from "@mui/icons-material"
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography
} from "@mui/material"

import DialogProvider from "@components/dialog_provider"
import PageHeader from "@components/page_header"
import Switch from "@components/switch"
import { PRIMARY } from "@constants/colors"
import { useCustomerPageContext } from "@contexts/customer"
import {
  AUTO_FORMALIZE_INVOICES_VALUES,
  AutoFormalizeInvoicesPreference,
  getPreferences,
  updateAccountApproval,
  updateAutoFormalizeInvoices
} from "@domain/customer/list"
import { showErrorToast } from "@utils/toast"
import { useEffect, useState } from "react"
import styles from "./styles"

const AUTO_FORMALIZE_LABEL_KEY: Record<
  AutoFormalizeInvoicesPreference,
  string
> = {
  no: "auto_formalize_no",
  yes: "auto_formalize_yes",
  yesOnlyTr: "auto_formalize_yes_only_tr",
  yesOnlyNotTr: "auto_formalize_yes_only_not_tr"
}

export default function CustomerPageHeader() {
  const { isLeftDrawerOpen, toggleIsLeftDrawerOpen, translate } =
    useCustomerPageContext()

  const [refAccount, setRefAccount] = useState(false)
  const [autoFormalize, setAutoFormalize] =
    useState<AutoFormalizeInvoicesPreference>("no")
  const [pendingValue, setPendingValue] =
    useState<AutoFormalizeInvoicesPreference | null>(null)
  const [isConfirmLoading, setIsConfirmLoading] = useState(false)

  async function fetchRef() {
    const { isSuccess, errorMessage, data } = await getPreferences()
    if (!isSuccess && !!errorMessage) {
      return
    }
    setRefAccount(data?.data.requireAccountApproval as boolean)
    const fetched = (data?.data as { autoFormalizeInvoices?: string })
      ?.autoFormalizeInvoices
    if (
      fetched &&
      AUTO_FORMALIZE_INVOICES_VALUES.includes(
        fetched as AutoFormalizeInvoicesPreference
      )
    ) {
      setAutoFormalize(fetched as AutoFormalizeInvoicesPreference)
    }
  }
  async function handleApprovalChange() {
    const { isSuccess, errorMessage, data } =
      await updateAccountApproval(!refAccount)
    if (!isSuccess && !!errorMessage) {
      showErrorToast(errorMessage)
      return
    }
    setRefAccount(data?.data.requireAccountApproval as boolean)
  }

  function handleAutoFormalizeSelect(next: AutoFormalizeInvoicesPreference) {
    if (next === autoFormalize) return
    setPendingValue(next)
  }

  async function handleConfirmAutoFormalize() {
    if (!pendingValue) return
    setIsConfirmLoading(true)
    try {
      const { isSuccess, errorMessage, data } =
        await updateAutoFormalizeInvoices(pendingValue)
      if (!isSuccess && !!errorMessage) {
        showErrorToast(errorMessage)
        return
      }
      const updated = (data?.data as { autoFormalizeInvoices?: string })
        ?.autoFormalizeInvoices
      if (
        updated &&
        AUTO_FORMALIZE_INVOICES_VALUES.includes(
          updated as AutoFormalizeInvoicesPreference
        )
      ) {
        setAutoFormalize(updated as AutoFormalizeInvoicesPreference)
      } else {
        setAutoFormalize(pendingValue)
      }
      setPendingValue(null)
    } finally {
      setIsConfirmLoading(false)
    }
  }

  function handleCancelAutoFormalize() {
    setPendingValue(null)
  }

  useEffect(() => {
    fetchRef()
  }, [])

  return (
    <PageHeader sx={styles.content} isLeftPanelOpen={isLeftDrawerOpen}>
      <Stack direction={"row"} alignItems={"center"} columnGap={1}>
        <IconButton
          onClick={toggleIsLeftDrawerOpen}
          disabled={!!isLeftDrawerOpen}
          disableRipple
          sx={{
            display: !!isLeftDrawerOpen ? "none" : "block",
            pl: 0,
            ":hover": {
              color: PRIMARY
            }
          }}
        >
          <Menu />
        </IconButton>
        <Typography variant="h6">{translate("customers")}</Typography>
      </Stack>
      <Stack
        direction={"row"}
        alignItems={"center"}
        gap={2}
        sx={{
          position: "absolute",
          right: isLeftDrawerOpen ? "calc(50px + 1rem)" : "1rem",
          transition: "right 0.3s ease-in-out"
        }}
      >
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <Typography variant="body2">
            {translate("auto_formalize_invoices")}
          </Typography>
          <FormControl size="small">
            <Select
              value={autoFormalize}
              onChange={(event) =>
                handleAutoFormalizeSelect(
                  event.target.value as AutoFormalizeInvoicesPreference
                )
              }
              sx={{ minWidth: 140 }}
            >
              {AUTO_FORMALIZE_INVOICES_VALUES.map((value) => (
                <MenuItem key={value} value={value}>
                  {translate(AUTO_FORMALIZE_LABEL_KEY[value])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <Typography variant="body2">
            {translate("account_approuval")}
          </Typography>
          <Switch checked={refAccount} onChange={handleApprovalChange} />
        </Stack>
      </Stack>
      <DialogProvider
        title={translate("auto_formalize_confirm_title")}
        isOpen={pendingValue !== null}
        setClose={handleCancelAutoFormalize}
        size="medium"
        leftButton={{
          text: translate("cancel"),
          color: "secondary",
          onClick: handleCancelAutoFormalize,
          disabled: isConfirmLoading
        }}
        rightButton={{
          text: translate("auto_formalize_confirm_action"),
          color: "error",
          onClick: handleConfirmAutoFormalize,
          loading: isConfirmLoading
        }}
      >
        <Box sx={{ whiteSpace: "pre-line" }}>
          <Typography variant="body2" color="error" fontWeight={600} mb={1}>
            {pendingValue
              ? translate(AUTO_FORMALIZE_LABEL_KEY[pendingValue])
              : ""}
          </Typography>
          <Typography variant="body2">
            {translate("auto_formalize_confirm_message")}
          </Typography>
        </Box>
      </DialogProvider>
    </PageHeader>
  )
}
